import React, { useEffect, useMemo, useState } from "react";
import { Box, styled, Typography, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import { sidebarRoutesObjects } from "./SidebarRoutesObjects";
import { useCheckPermission } from "src/hooks/useCheckPermission";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SidebarRoutes from "./SidebarRoutes";
import { sidebarRoutesObjectsCashier } from "./SidebarRoutesObjectsCashier";
import { usePermission, useUser } from "src/contexts/GlobalContext";

const ITEM_HEIGHT_PX = 35;

const SidebarCategories = ({
  category,
  Icon,
  isParent = true,
  route = null,
  localEnter,
  isMinimized,
}) => {
  const { checkPermission } = useCheckPermission();
  const permissions = usePermission();
  const [isOpen, setIsOpen] = useState(false);
  const [routeHight, setRouteHight] = useState(0);
  const theme = useTheme();
  const user = useUser();
  const { pathname } = useLocation();

  const menuItems = useMemo(() => {
    const tempMenuItems = [];
    Object.keys(sidebarRoutesObjects)
      .filter(
        (routeKey) =>
          sidebarRoutesObjects[routeKey].categorySlug === category.slug
      )
      .forEach((routeKey) => {
        if (checkPermission(sidebarRoutesObjects[routeKey].permissionKey)) {
          tempMenuItems.push(sidebarRoutesObjects[routeKey]);
        }
      });
    return tempMenuItems;
    // permissions is the real dependency; checkPermission is recreated every render
  }, [category.slug, permissions]);

  const cashierMenuItems = useMemo(() => {
    const tempMenuItems = [];
    Object.keys(sidebarRoutesObjectsCashier)
      .filter(
        (routeKey) =>
          sidebarRoutesObjectsCashier[routeKey].categorySlug === category.slug
      )
      .forEach((routeKey) => {
        if (
          checkPermission(sidebarRoutesObjectsCashier[routeKey].permissionKey)
        ) {
          tempMenuItems.push(sidebarRoutesObjectsCashier[routeKey]);
        }
      });
    return tempMenuItems;
  }, [category.slug, permissions]);

  useEffect(() => {
    const section = pathname.split("/")[2];
    if (category.slug === section) {
      setIsOpen(true);
    }
  }, [category.slug, pathname]);

  useEffect(() => {
    if (!isOpen) {
      setRouteHight(0);
      return;
    }
    const count =
      user?.role?.name === "CASHIER"
        ? cashierMenuItems.length
        : menuItems.length;
    setRouteHight(count * ITEM_HEIGHT_PX);
  }, [isOpen, menuItems, cashierMenuItems, user?.role?.name]);

  return isParent ? (
    <SidebarWrapper
      theme={theme}
      isopen={isOpen ? "true" : "false"}
      routehight={routeHight}
    >
      <Box
        className="header"
        onClick={() => setIsOpen((prevState) => !prevState)}
      >
        <Box display="flex" flexDirection="row" alignItems="center">
          <Icon className="icon" />
          {isMinimized || localEnter ? (
            <Typography className="category-name" ml={1}>
              {category.name}
            </Typography>
          ) : (
            <></>
          )}
        </Box>

        {isMinimized || localEnter ? (
          isOpen ? (
            <KeyboardArrowDownIcon />
          ) : (
            <KeyboardArrowLeftIcon />
          )
        ) : (
          <></>
        )}
      </Box>
      {isMinimized || localEnter ? (
        <Box className="body">
          {user?.role?.name != "DRIVER" && user?.role?.name != "CASHIER" ? (
            menuItems.map((item, index) => (
              <SidebarRoutes
                key={item.link || index}
                route={item}
                Icon={item.icon}
                isMinimized={isMinimized}
                localEnter={localEnter}
              />
            ))
          ) : (
            <></>
          )}
          {user?.role?.name == "CASHIER" ? (
            cashierMenuItems.map((item, index) => (
              <SidebarRoutes
                key={item.link || index}
                route={item}
                Icon={item.icon}
                isMinimized={isMinimized}
                localEnter={localEnter}
              />
            ))
          ) : (
            <></>
          )}
        </Box>
      ) : (
        <></>
      )}
    </SidebarWrapper>
  ) : checkPermission(route.permissionKey) ? (
    <SidebarRoutes
      route={route}
      Icon={Icon}
      isMinimized={isMinimized}
      localEnter={localEnter}
    />
  ) : (
    <></>
  );
};

export default SidebarCategories;

const SidebarWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: space-between;
  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 10px;
    padding-left: 10px;
    cursor: pointer;
    opacity: ${(props) => (props.isopen === "true" ? "0.8" : "0.4")};
    color: ${(props) => (props.isopen === "true" ? "white" : "lightgray")};
    :hover {
      color: white;
    }
    .active {
      background: #f2f5f9;
      color: #7e6fd0;
    }
    .category-name {
      font-size: 13px;
      font-weight: 600;
    }
  }
  .body {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    /* Pixel height (not auto) so the open/close transition actually reveals items */
    height: ${(props) =>
      props.isopen === "true" ? `${props.routehight || 0}px` : "0px"};
    transition: height 0.3s ease;
  }
`;
